import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withXhr
} from "@angular/common/http";
import {
  enableProdMode,
  inject,
  isDevMode,
  provideAppInitializer
} from "@angular/core";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideRouter } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { type ChatMember, provideFitChat } from "@fit-epd/chat";
import { provideTransloco } from "@jsverse/transloco";
import { provideOAuthClient } from "angular-oauth2-oidc";
import { AppComponent } from "app/app.component";
import { routes } from "app/app.routes";
import { AuthService } from "app/shared/auth/auth.service";
import { httpLanguageInterceptor } from "app/shared/http-language.interceptor";
import { httpMaintenanceInterceptor } from "app/shared/http-maintenance.interceptor";
import { TranslocoHttpLoader } from "app/transloco-loader";
import { environment } from "environments/environment";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import { firstValueFrom } from "rxjs";

import { APP_CONFIG, AppConfig } from "./app/app.config";

const configHeaders = new Headers();
configHeaders.append("pragma", "no-cache");
configHeaders.append("cache-control", "no-cache");

const configInit = {
  method: "GET",
  headers: configHeaders
};

const configRequest = new Request(environment.configFile);

fetch(configRequest, configInit)
  .then((response) => response.json())
  .then((config: AppConfig) => {
    if (environment.production) {
      enableProdMode();
    }

    return bootstrapApplication(AppComponent, {
      providers: [
        provideHttpClient(
          withInterceptors([
            httpLanguageInterceptor,
            httpMaintenanceInterceptor
          ]),
          withXhr()
        ),
        provideOAuthClient(),
        provideAppInitializer(() => inject(AuthService).initialize()),
        provideRouter(routes),
        { provide: APP_CONFIG, useValue: config },
        provideFitChat(() => {
          const appConfig = inject<AppConfig>(APP_CONFIG);
          const auth = inject(AuthService);
          const http = inject(HttpClient);
          return {
            apiBaseUrl: appConfig.ApiOrigin,
            accessTokenFactory: () => auth.getAccessToken(),
            mode: "patient",
            drawerTop: "76px",
            drawerBottom: "12px",
            canCreateRooms: false,
            canManageMembers: false,
            canModerateJoinRequests: false,
            canRequestConversation: true,
            loadProfessionals: async () =>
              firstValueFrom(
                http.get<{ id: number; displayName: string }[]>(
                  `${appConfig.ApiOrigin.replace(/\/$/, "")}/api/chat/professionals`,
                  { params: { take: 100 } }
                )
              ),
            loadMemberAvatar: async (member: ChatMember) => {
              const apiBaseUrl = appConfig.ApiOrigin.replace(/\/$/, "");
              const path =
                member.memberType === "Patient"
                  ? `/api/patient/${member.memberId}/profilepicture`
                  : `/api/users/${member.memberId}/profilepicture`;
              const picture = await firstValueFrom(
                http.get<{ picture?: string } | null>(`${apiBaseUrl}${path}`)
              );
              return picture?.picture
                ? `data:image/jpeg;base64,${picture.picture}`
                : null;
            }
          };
        }),
        provideCharts(withDefaultRegisterables()),
        provideTransloco({
          config: {
            availableLangs: ["nl", "en"],
            defaultLang: "en",
            reRenderOnLangChange: true,
            prodMode: environment.production,
            fallbackLang: "en",
            missingHandler: {
              useFallbackTranslation: true
            }
          },
          loader: TranslocoHttpLoader
        }),
        provideServiceWorker("ngsw-worker.js", {
          enabled: !isDevMode(),
          registrationStrategy: "registerWhenStable:30000"
        })
      ]
    });
  });
